import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useProgress } from '../hooks/useProgress';
import { useAuth } from '../hooks/useAuth';
import { Nav } from '../components/Nav';
import { ProgressBar } from '../components/ProgressBar';
import { Celebration } from '../components/Celebration';
import { updateVideoProgress, getBooking } from '../utils/api';
import { DAYS, C } from '../utils/constants';
import { trackCourseVideo } from '../utils/courseVideoTracker';

const BMS_BOOKING_URL = 'https://bookings.benmilston.com/book/breakthrough-mapping-session';

export const DayVideo = () => {
  const { dayNum } = useParams();
  const dayNumber = parseInt(dayNum, 10);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getDayProgress, completedDays, refetch } = useProgress();

  const day = DAYS.find(d => d.num === dayNumber);
  const progressData = getDayProgress(dayNumber);

  const [showCelebration, setShowCelebration] = useState(false);
  const [videoComplete, setVideoComplete] = useState(progressData?.video_complete || false);
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    if (progressData?.video_complete) {
      setVideoComplete(true);
    }
  }, [progressData]);

  // Fetch booking status for Day 6 CTA
  useEffect(() => {
    if (dayNumber !== 6) return;
    getBooking().then(res => setBooking(res.data)).catch(() => {});
  }, [dayNumber]);

  // Record how the client watches this video (utils/courseVideoTracker.js).
  // Recording only — never affects playback or whether the day counts as done.
  useEffect(() => {
    if (!day?.wistiaId) return undefined;
    return trackCourseVideo(day?.wistiaId, dayNumber);
  }, [day?.wistiaId, dayNumber]);

  // Wistia embed script injection (idempotent)
  useEffect(() => {
    if (!day?.wistiaId) return;
    if (!document.querySelector('script[src="https://fast.wistia.com/assets/external/E-v1.js"]')) {
      const s = document.createElement('script');
      s.src = 'https://fast.wistia.com/assets/external/E-v1.js';
      s.async = true;
      document.head.appendChild(s);
    }
    const mediaSrc = `https://fast.wistia.com/embed/medias/${day.wistiaId}.jsonp`;
    if (!document.querySelector(`script[src="${mediaSrc}"]`)) {
      const m = document.createElement('script');
      m.src = mediaSrc;
      m.async = true;
      document.head.appendChild(m);
    }
  }, [day?.wistiaId]);

  const handleContinue = async () => {
    if (!videoComplete) {
      setVideoComplete(true);
      await updateVideoProgress(dayNumber, 100).catch(() => {});
      await refetch();
      setShowCelebration(true);
    } else {
      navigate(`/day/${dayNumber}/journal`);
    }
  };

  const handleCelebrationClose = async () => {
    setShowCelebration(false);
    await refetch();
    navigate(`/day/${dayNumber}/journal`);
  };

  const handleAdmin = () => navigate('/admin');
  const totalSteps = day?.hasMeditation ? 3 : 2;

  if (!day) {
    return (
      <div style={{ minHeight: '100vh', background: C.cream }}>
        <Nav user={user} onAdmin={handleAdmin} />
        <div style={{ textAlign: 'center', padding: '80px 24px', color: C.terracotta }}>
          Day not found.
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.cream }}>
      <Nav user={user} onAdmin={handleAdmin} />
      <ProgressBar completedDays={completedDays} />

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '28px 16px 64px' }}>
        {/* Breadcrumb */}
        <nav style={{ fontSize: '0.8rem', color: C.terracotta, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 6 }}>
          <Link to="/dashboard" style={{ color: C.terracotta, textDecoration: 'none' }}>
            Dashboard
          </Link>
          <span style={{ opacity: 0.5 }}>›</span>
          <span style={{ color: C.brown, fontWeight: 600 }}>Day {dayNumber} · Video</span>
        </nav>

        {/* Title */}
        <div className="animate-in" style={{ marginBottom: 24 }}>
          <div style={{
            fontFamily: "'Forum', serif",
            fontSize: '0.7rem',
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: C.gold,
            marginBottom: 6,
          }}>
            Step 1 of {totalSteps}
          </div>
          <h1 style={{
            fontFamily: "'Forum', serif",
            color: C.brown,
            fontSize: 'clamp(1.4rem, 4vw, 1.9rem)',
            letterSpacing: '0.02em',
            marginBottom: 4,
          }}>
            Day {dayNumber}: {day.title}
          </h1>
          <p style={{ color: C.terracotta, fontSize: '0.88rem' }}>
            Watch today's training, then continue to your journal
            {day.duration && <span style={{ color: C.terracotta, opacity: 0.75 }}> · Video length - {day.duration}</span>}
            {videoComplete && <span style={{ color: C.amber, fontWeight: 600 }}> — you've already completed this day ✓</span>}
          </p>
        </div>

        {/* Video area */}
        <div style={{
          borderRadius: 16,
          overflow: 'hidden',
          aspectRatio: '16/9',
          marginBottom: 24,
          boxShadow: '0 8px 40px rgba(0,0,0,0.2)',
          background: '#1a0800',
          position: 'relative',
        }}>
          {day.wistiaId ? (
            <div
              className={`wistia_embed wistia_async_${day.wistiaId}`}
              style={{ width: '100%', height: '100%', position: 'relative' }}
            >
              &nbsp;
            </div>
          ) : (
            /* Coming soon placeholder */
            <div style={{
              width: '100%',
              height: '100%',
              background: `linear-gradient(135deg, #1a0800 0%, #2d1204 50%, #1a0800 100%)`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 16,
            }}>
              <div style={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                background: 'rgba(245,166,35,0.15)',
                border: '2px solid rgba(245,166,35,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '2rem',
              }}>
                🎬
              </div>
              <div style={{ textAlign: 'center' }}>
                <p style={{
                  fontFamily: "'Forum', serif",
                  color: C.gold,
                  fontSize: '1.1rem',
                  letterSpacing: '0.06em',
                  marginBottom: 6,
                }}>
                  Video Coming Soon
                </p>
                <p style={{
                  color: 'rgba(255,255,255,0.4)',
                  fontSize: '0.8rem',
                }}>
                  Day {dayNumber}: {day.title}
                </p>
              </div>
              {/* Watermark */}
              <div style={{
                position: 'absolute',
                bottom: 14,
                left: 18,
                fontFamily: "'Forum', serif",
                color: 'rgba(255,255,255,0.2)',
                fontSize: '0.7rem',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
              }}>
                Recondition Yourself · Day {dayNumber}
              </div>
            </div>
          )}
        </div>

        {/* Day 6 BMS Booking CTA — only shown when not yet booked */}
        {dayNumber === 6 && !booking?.callBooked && (
          <div style={{
            background: `linear-gradient(135deg, #1a0800 0%, ${C.brown} 100%)`,
            borderRadius: 16,
            padding: '24px 28px',
            marginBottom: 24,
            boxShadow: '0 6px 32px rgba(61,31,2,0.2)',
            textAlign: 'center',
          }}>
            <div style={{
              fontFamily: "'Forum', serif",
              fontSize: '0.72rem',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: C.gold,
              marginBottom: 8,
            }}>
              Included In Your Program
            </div>
            <div style={{
              fontFamily: "'Forum', serif",
              color: '#fff',
              fontSize: '1.2rem',
              letterSpacing: '0.02em',
              marginBottom: 4,
            }}>
              Breakthrough Mapping Session
            </div>
            <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.84rem', marginBottom: 20 }}>
              90 min · 1:1 with Ben · Via Zoom — Book your spot while you watch.
            </p>
            <a
              href={`${BMS_BOOKING_URL}?days_remaining=${Math.max(0, 6 - completedDays)}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                fontFamily: "'Forum', serif",
                background: `linear-gradient(135deg, ${C.amber}, ${C.gold})`,
                color: C.darkBrown,
                padding: '13px 28px',
                borderRadius: 8,
                fontSize: '1rem',
                fontWeight: 700,
                textDecoration: 'none',
                boxShadow: '0 4px 16px rgba(245,166,35,0.4)',
              }}
            >
              Book Your Breakthrough Mapping Session →
            </a>
          </div>
        )}

        {/* Continue to Journal — always available */}
        <button
          className="btn-primary"
          onClick={handleContinue}
          style={{ width: '100%' }}
        >
          {[3, 4, 5].includes(dayNumber) ? 'Continue to Journal + Guided Meditation →' : 'Continue to Journal →'}
        </button>
      </div>

      {showCelebration && <Celebration onClose={handleCelebrationClose} />}
    </div>
  );
};
